/**
 * Incasarile pe ecranul Rambursuri curieri: tabelul cu diferenta incasat–facturat, trimiterea
 * selectiei in EA cu confirmare si viramentele pe zile (12.10.2026).
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { Pastila } from '../../../components/ui/Pastila'
import type { Ton } from '../../../components/ui/TonuriB4L'
import {
  type PrevizualizareSelectie,
  type Ramburs,
  type ViramenteResp,
  STATUS_LABEL,
  lei,
  numeCurier,
  zi,
} from './tipuri'

const TON_STARE: Record<string, Ton> = {
  exceptie: 'galben',
  potrivit: 'albastru',
  in_trimitere: 'albastru',
  trimis: 'verde',
  deja_incasat: 'gri',
  inchis_an_vechi: 'gri',
}
const ETICHETA: Record<string, string> = {
  ...STATUS_LABEL,
  exceptie: 'De potrivit',
  potrivit: 'De trimis',
  inchis_an_vechi: 'Închis',
}

export function TabelIncasari({
  randuri,
  selectie,
  onSelectie,
  actiune,
  coloane = 'complet',
}: {
  randuri: Ramburs[]
  /** Cu selectie: casute de bifat pe fiecare rand (doar cine poate trimite in EA). */
  selectie?: Set<number>
  onSelectie?: (s: Set<number>) => void
  /** Un buton pe rand — ex. „Potriveste…". */
  actiune?: (r: Ramburs) => React.ReactNode
  /** `potrivire` = coloanele care conteaza cand factura lipseste: suma si motivul. */
  coloane?: 'complet' | 'potrivire'
}) {
  // Se bifeaza doar ce se poate trimite acum; un rand „in trimitere" chiar acum e al altui proces.
  const bifabile = randuri.filter((r) => r.deTrimis)
  const toate = selectie != null && bifabile.length > 0 && bifabile.every((r) => selectie.has(r.id))
  const comuta = (id: number) => {
    if (!selectie || !onSelectie) return
    const s = new Set(selectie)
    if (s.has(id)) s.delete(id)
    else s.add(id)
    onSelectie(s)
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-b4l-border text-left text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
            {selectie && (
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Selectează toate"
                  checked={toate}
                  onChange={() =>
                    onSelectie?.(toate ? new Set() : new Set(bifabile.map((r) => r.id)))
                  }
                />
              </th>
            )}
            <th className="px-3 py-2">Virament</th>
            <th className="px-3 py-2">AWB</th>
            <th className="px-3 py-2">Client · comandă</th>
            {coloane === 'complet' ? (
              <>
                <th className="px-3 py-2">Factură</th>
                <th className="px-3 py-2 text-right">Încasat</th>
                <th className="px-3 py-2 text-right">Facturat</th>
                <th className="px-3 py-2 text-right">Diferență</th>
                <th className="px-3 py-2">Stare</th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 text-right">Încasat</th>
                <th className="px-3 py-2">De ce</th>
              </>
            )}
            {actiune && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {randuri.map((r) => {
            const dif =
              r.invoiceTotal == null ? null : +(r.amountCollected - r.invoiceTotal).toFixed(2)
            return (
              <tr key={r.id} className="border-b border-b4l-border align-top last:border-0">
                {selectie && (
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={`Selectează ${r.awbNo}`}
                      checked={selectie.has(r.id)}
                      disabled={!r.deTrimis}
                      title={r.deTrimis ? undefined : 'Se trimite chiar acum în EA'}
                      onChange={() => comuta(r.id)}
                    />
                  </td>
                )}
                <td className="px-3 py-2.5 tabular-nums">{zi(r.transferDate)}</td>
                <td className="px-3 py-2.5">
                  <div className="font-mono text-xs">{r.awbNo}</div>
                  <div className="text-xs text-b4l-text-dim">{numeCurier(r.courier)}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{r.partnerName ?? r.recipientName ?? '—'}</div>
                  <div className="text-xs text-b4l-text-dim">{r.comandaNo ?? '—'}</div>
                </td>
                {coloane === 'complet' ? (
                  <>
                    <td className="px-3 py-2.5 font-mono text-xs">{r.eaDocNo ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {lei(r.amountCollected)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {r.invoiceTotal == null ? '—' : lei(r.invoiceTotal)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {dif == null ? (
                        <span className="text-b4l-text-dim">—</span>
                      ) : dif === 0 ? (
                        <span className="text-b4l-text-dim">0</span>
                      ) : (
                        <span
                          className={
                            dif < 0 ? 'font-semibold text-b4l-red-dark' : 'text-b4l-amber-dark'
                          }
                          title={
                            dif > 0
                              ? 'Curierul a încasat mai mult decât factura — de regulă o taxă plătită de client. Factura se stinge pe valoarea ei.'
                              : 'Curierul a încasat mai puțin decât factura.'
                          }
                        >
                          {dif > 0 ? '+' : ''}
                          {lei(dif)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Pastila ton={TON_STARE[r.status] ?? 'gri'}>
                        {r.status === 'in_trimitere' && r.deTrimis
                          ? 'Rămasă în trimitere — de reluat'
                          : (ETICHETA[r.status] ?? r.status)}
                      </Pastila>
                      {r.closedReason && (
                        <div className="mt-1 text-xs text-b4l-text-dim">{r.closedReason}</div>
                      )}
                      {/* De ce n-a plecat in EA: factura stinsa partial, refuzul EA etc. */}
                      {r.status === 'potrivit' && r.eaResponse && (
                        <div
                          className="mt-1 max-w-xs text-xs text-b4l-amber-dark"
                          title={r.eaResponse}
                        >
                          {r.eaResponse.length > 140
                            ? `${r.eaResponse.slice(0, 140)}…`
                            : r.eaResponse}
                        </div>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {lei(r.amountCollected)}
                    </td>
                    <td className="px-3 py-2.5">{r.matchErrorLabel ?? '—'}</td>
                  </>
                )}
                {actiune && (
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex justify-end gap-1.5">{actiune(r)}</div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Bara de jos cu selectia si butonul de trimitere. */
export function BaraTrimitere({
  randuri,
  selectie,
  onTrimis,
}: {
  randuri: Ramburs[]
  selectie: Set<number>
  onTrimis: () => void
}) {
  const [confirmare, setConfirmare] = useState(false)
  const alese = randuri.filter((r) => selectie.has(r.id))
  const suma = alese.reduce((t, r) => t + (r.invoiceTotal ?? r.amountCollected), 0)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-b4l-border px-4 py-3">
      <span className="text-sm">
        {alese.length ? (
          <>
            Selectate:{' '}
            <b className="tabular-nums">
              {alese.length} · {lei(suma)} lei
            </b>
          </>
        ) : (
          <span className="text-b4l-text-muted">Bifează încasările de trimis</span>
        )}
      </span>
      <Button disabled={!alese.length} onClick={() => setConfirmare(true)}>
        Trimite selectate în EA…
      </Button>
      {confirmare && (
        <ConfirmaTrimitere
          ids={alese.map((r) => r.id)}
          onGata={(trimis) => {
            setConfirmare(false)
            if (trimis) onTrimis()
          }}
        />
      )}
    </div>
  )
}

/**
 * Confirmarea trimiterii in EA, cu verificarea facuta INAINTE in EA: cate pleaca, cate erau deja
 * incasate, cate sunt stinse partial — aceleasi reguli ca trimiterea (F1–F4, #798).
 */
function ConfirmaTrimitere({ ids, onGata }: { ids: number[]; onGata: (trimis: boolean) => void }) {
  const qc = useQueryClient()
  const prev = useQuery<PrevizualizareSelectie>({
    queryKey: ['fan', 'previzualizare', ids],
    queryFn: async () => (await api.post('/fan/trimite/previzualizare', { ids })).data,
    retry: false,
  })
  const trimite = useMutation({
    mutationFn: async () =>
      (await api.post<{ trimise: number; esuate: number }>('/fan/trimite', { ids })).data,
    onSuccess: (r) => {
      if (r.esuate) toast.warning(`${r.trimise} trimise în EA, ${r.esuate} nu — motivul e pe rând.`)
      else toast.success(`${r.trimise} încasări trimise în EA`)
      qc.invalidateQueries({ queryKey: ['fan'] })
      onGata(true)
    },
    onError: () => toast.error('Trimiterea a eșuat. Nimic nu s-a marcat ca trimis.'),
  })
  const p = prev.data
  return (
    <Modal
      open
      onClose={() => onGata(false)}
      title={`Trimiți ${ids.length} încasări în ExpertAccounts?`}
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={() => onGata(false)}>
            Renunță
          </Button>
          <Button
            onClick={() => trimite.mutate()}
            loading={trimite.isPending}
            disabled={!p || p.seTrimit.n === 0}
          >
            {p ? `Trimite ${p.seTrimit.n} în EA` : 'Trimite'}
          </Button>
        </>
      }
    >
      {prev.isLoading && (
        <p className="text-sm text-b4l-text-muted">Se verifică facturile în EA…</p>
      )}
      {prev.isError && (
        <p className="text-sm text-b4l-red-dark">
          Facturile nu se pot verifica acum în EA, așa că nu se trimite nimic. Încearcă din nou
          peste câteva minute.
        </p>
      )}
      {p && (
        <div className="grid gap-3 text-sm">
          <p className="text-b4l-text-muted">Am verificat acum în EA fiecare factură:</p>
          <div className="grid grid-cols-3 gap-2">
            <Cifra n={p.seTrimit.n} eticheta="se trimit" ton="text-b4l-green-dark" />
            <Cifra n={p.dejaIncasate.n} eticheta="erau deja încasate" ton="text-b4l-text-muted" />
            <Cifra n={p.partiale.n} eticheta="stinse parțial" ton="text-b4l-amber-dark" />
          </div>
          <p>
            Se trimit <b className="tabular-nums">{lei(p.seTrimit.suma)} lei</b>. Cele deja încasate
            sau stinse parțial nu pleacă; rămân în listă cu motivul scris.
            {p.neeligibile > 0 && ` ${p.neeligibile} nu mai erau de trimis.`}
          </p>
        </div>
      )}
    </Modal>
  )
}

function Cifra({ n, eticheta, ton }: { n: number; eticheta: string; ton: string }) {
  return (
    <div className="rounded-lg bg-b4l-bg-soft px-3 py-2">
      <b className={`block text-lg tabular-nums ${ton}`}>{n}</b>
      <span className="text-xs text-b4l-text-muted">{eticheta}</span>
    </div>
  )
}

/**
 * Viramentele pe zile, pliabile sub incasari: banii virati de curier fata de extrasul bancar.
 * Fosta fila „Viramente" (12.10.2026: „putem simplifica Viramente"). O zi dupa ultimul extras
 * incarcat nu e o pierdere — spune „extrasul n-a sosit inca".
 */
export function ViramentePeZile({ courier }: { courier: 'FAN' | 'SAMEDAY' }) {
  const [deschis, setDeschis] = useState(false)
  const { data, isError } = useQuery<ViramenteResp>({
    queryKey: ['fan', 'viramente', courier],
    queryFn: async () => (await api.get('/fan/viramente', { params: { courier } })).data,
    enabled: deschis,
  })
  const confirmatPana = data?.extrasPanaLa
  return (
    <details
      className="border-t border-b4l-border"
      onToggle={(e) => setDeschis((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer px-4 py-3 text-sm text-b4l-text-muted hover:text-b4l-text">
        Viramente pe zile ({courier === 'FAN' ? 'FAN' : 'Sameday'}) — banii virați de curier față de
        extrasul bancar
      </summary>
      {isError ? (
        <p className="px-4 pb-4 text-sm text-b4l-red-dark">Nu am putut aduce viramentele.</p>
      ) : !data ? (
        <p className="px-4 pb-4 text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
                <th className="px-3 py-2">Zi</th>
                <th className="px-3 py-2 text-right">Colete</th>
                <th className="px-3 py-2 text-right">Virat de curier</th>
                <th className="px-3 py-2 text-right">În extras</th>
                <th className="px-3 py-2">Stare</th>
              </tr>
            </thead>
            <tbody>
              {data.zile.map((z) => {
                const nesosit = !confirmatPana || z.date > confirmatPana
                return (
                  <tr key={z.date} className="border-b border-b4l-border last:border-0">
                    <td className="px-3 py-2 tabular-nums">{zi(z.date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{z.incasari}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei(z.colectat)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {z.status === 'confirmat' || !nesosit ? lei(z.primit) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {z.status === 'confirmat' ? (
                        <Pastila ton="verde">Confirmat</Pastila>
                      ) : nesosit ? (
                        <Pastila ton="gri">Extrasul n-a sosit încă</Pastila>
                      ) : (
                        <Pastila ton="rosu">Diferență {lei(z.diferenta)} lei</Pastila>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </details>
  )
}
